import { StateService } from '@uirouter/core';
import { IComponentController, IComponentOptions, IScope } from 'angular';
import { StateParams, Transition, TransitionService } from 'angular-ui-router';
import {
    FONT_CATEGORY_OPTIONS,
    FONT_LANGUAGE_OPTIONS,
    FONT_POSTURE_OPTIONS,
    FONT_RECOMMENDED_FUNCTION_OPTIONS,
    FONT_VAR_AXIS_OPTIONS,
    FontListParams,
    FoundryService,
    IFoundryModel,
    debounce,
} from '../../../tn.common';
import {
    FontListParamsStateParamsResolver,
    FontListParamsToStateParams,
} from '../../../tn.common/providers';
import { PeopleService } from '../../../tn.fonts/tn.fonts.service/people';
import template from './catalog-page-sidebar.html';
import angular from 'angular';

export class CatalogSidebarController implements IComponentController {
    // bindings
    filters: FontListParams = {};
    foundry?: IFoundryModel;

    // locals
    search = '';
    tn_weight_min = 0;
    tn_weight_max = 900;
    tn_width_min = 0;
    tn_width_max = 900;

    foundries: IFoundryModel[] = [];

    recommendedFunctions = FONT_RECOMMENDED_FUNCTION_OPTIONS;
    postures = FONT_POSTURE_OPTIONS;
    languages = FONT_LANGUAGE_OPTIONS;
    categories = FONT_CATEGORY_OPTIONS;
    variableAxes = FONT_VAR_AXIS_OPTIONS;

    static $inject = [
        'foundryService',
        'tn.people.service',
        '$state',
        '$stateParams',
        '$transitions',
        '$scope',
    ];

    constructor(
        protected foundryService: FoundryService,
        protected peopleService: PeopleService,
        protected $state: StateService,
        protected $stateParams: StateParams,
        protected $transitions: TransitionService,
        protected $scope: IScope
    ) {
        const onTransitionSuccess = (transition: Transition) => {
            const params = transition.params();
            this.onStateParamsChanged(params);
        };

        this.$transitions.onSuccess(
            { to: 'home.fonts.listing' },
            onTransitionSuccess
        );
        this.$transitions.onSuccess(
            { to: 'home.foundry.fonts.listing' },
            onTransitionSuccess
        );

        this.$scope.$watchGroup(
            [
                'vm.tn_weight_min',
                'vm.tn_weight_max',
                'vm.tn_width_min',
                'vm.tn_width_max',
            ],
            debounce(() => {
                this.modifyState({
                    tn_weight_min: this.tn_weight_min,
                    tn_weight_max: this.tn_weight_max,
                    tn_width_min: this.tn_width_min,
                    tn_width_max: this.tn_width_max,
                });
            }, 300)
        );

        this.foundryService.getDjangoFoundries().then((foundries) => {
            this.foundries = foundries;
        });
    }

    onStateParamsChanged(params: Partial<StateParams>): void {
        const filters = FontListParamsStateParamsResolver(params);
        this.search = filters.search || '';
        this.tn_weight_min = filters.tn_weight_min || 0;
        this.tn_weight_max = filters.tn_weight_max || 900;
        this.tn_width_min = filters.tn_width_min || 0;
        this.tn_width_max = filters.tn_width_max || 900;
        this.filters = filters;
    }

    $onInit(): void {
        this.onStateParamsChanged(this.$stateParams);
    }

    modifyState(params: Partial<FontListParams>): void {
        // if params matches this.filter then do nothing.
        const changed = Object.keys(params).some((key) => {
            const param = params[key];
            const filter = this.filters[key];
            const equal = angular.equals(param, filter);
            return !equal;
        });
        if (!changed) {
            return;
        }

        const stateParams = FontListParamsToStateParams({
            ...this.filters,
            ...params,
            // reset to page 1 when filters change
            page: 1,
        });
        this.$state.go('.', stateParams);
    }

    clearFilter(filter: string): void {
        delete this.filters[filter];
        const stateParams = FontListParamsToStateParams(this.filters);
        // use inherit: false to ensure removed filters don't get preserved by inheritance
        this.$state.go(
            'home.fonts.listing',
            { ...stateParams },
            { inherit: false }
        );
    }

    hasFilter(key: keyof FontListParams, value: unknown): boolean {
        const filter = this.filters[key];
        if (filter == undefined) return false;
        const isArray = Array.isArray(filter);
        if (value == undefined)
            return isArray ? filter.length > 0 : filter !== '';
        return isArray
            ? (filter as unknown[]).includes(value)
            : filter === value;
    }

    goToFoundryFonts(foundry: IFoundryModel): void {
        if (this.foundry && this.foundry.slug === foundry.slug) {
            this.$state.go('home.fonts.listing');
        } else {
            this.$state.go('home.foundry.fonts.listing', {
                foundryEESubdomain: foundry.ee_subdomain,
            });
        }
    }

    clearFilters(): void {
        if (this.foundry) {
            this.$state.go(
                'home.foundry.fonts.listing',
                {
                    foundryEESubdomain: this.foundry.ee_subdomain,
                },
                { inherit: false }
            );
        } else {
            this.$state.go('home.fonts.listing', {}, { inherit: false });
        }
    }
}

export const CatalogSidebarComponent: IComponentOptions = {
    controller: CatalogSidebarController,
    controllerAs: 'vm',
    bindings: {
        filters: '<',
        foundry: '<?',
    },
    template,
};
